/**
 * Authentication hooks.
 *
 * The account always comes from the server. The client holds no credential at
 * all: the session is an `HttpOnly` cookie the browser attaches to same-origin
 * requests, so "am I signed in?" is answered by `GET /api/auth/me` rather than by
 * anything readable in JavaScript. The store only caches the last answer so the
 * header can render while a refresh is in flight.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { AdminCapability } from '@shared/constants'
import type { Locale } from '@shared/locales'
import type {
  CurrentUserResponse,
  RequestVerificationCodeResponse,
  SignInResponse,
  UserDto,
} from '@shared/types'

import { apiRequest, ApiError } from '../lib/api'
import { queryKeys } from '../lib/query'
import { referralFields } from '../lib/referral-capture'
import { clearAllDrafts } from '../stores/drafts'
import { useSessionStore } from '../stores/session'

export interface CurrentUser {
  user: UserDto | null
  isAuthenticated: boolean
  /** Account has been activated with an invite code. */
  isInvited: boolean
  /** Account holds an enabled administrator record. */
  isAdmin: boolean
  /** Effective capabilities resolved by the server. */
  capabilities: AdminCapability[]
  hydrating: boolean
}

/** Reads the cached session state plus the boot-time hydration flag. */
export function useCurrentUser(): CurrentUser {
  const user = useSessionStore((state) => state.user)
  const hydrating = useSessionStore((state) => state.hydrating)

  return {
    user,
    isAuthenticated: user !== null,
    isInvited: Boolean(user?.isInvited),
    isAdmin: Boolean(user?.isAdmin),
    capabilities: user?.capabilities ?? [],
    hydrating,
  }
}

/**
 * Loads the account the session cookie belongs to.
 *
 * A 401 is not an error state: it is the answer for an anonymous visitor, so the
 * store is emptied and `null` is returned instead of an exception. Any other
 * failure propagates, because a broken `/me` and a missing session are different
 * problems and only the second one may silently render the anonymous view.
 */
/**
 * True while the session is still unknown.
 *
 * The session cookie is invisible to JavaScript, so "signed out" can only be
 * concluded after `/api/auth/me` answers. A full page load starts with an empty
 * store, and a page that redirects on the first render therefore bounces a
 * signed-in visitor to the sign-in form — which is what a refresh of the join
 * wizard or the discussion used to do.
 */
export function useSessionUnknown(): boolean {
  const hydrating = useSessionStore((state) => state.hydrating)
  const me = useMe()
  return hydrating || me.isPending
}

export function useMe() {
  const setUser = useSessionStore((state) => state.setUser)
  const endHydration = useSessionStore((state) => state.endHydration)

  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      try {
        const result = await apiRequest<CurrentUserResponse>('/api/auth/me', {
          tolerateUnauthorized: true,
        })
        setUser(result.user)
        return result.user
      } catch (error) {
        // Only 401 means the credential is gone. Clearing the account on any
        // other failure would sign a signed-in visitor out because one request
        // happened to fail.
        if (error instanceof ApiError && error.status === 401) {
          setUser(null)
          return null
        }
        // The account is still unknown, but waiting is over: guards render the
        // failure with a retry instead of an endless spinner.
        endHydration()
        throw error
      }
    },
    // The probe runs on every page load and a 401 is already handled explicitly
    // above, so retrying only delays rendering the anonymous view.
    retry: false,
  })
}

export interface SendCodeInput {
  email: string
  locale: Locale
}

export function useSendCode() {
  const signedIn = useSignedIn()

  return useMutation({
    mutationFn: (input: SendCodeInput) =>
      apiRequest<RequestVerificationCodeResponse>('/api/auth/email/send', {
        method: 'POST',
        // The referral code rides along even though the production flow only
        // needs it later: in local development this request is the sign in, and
        // attribution has to happen in the request that creates the account.
        body: { email: input.email, locale: input.locale, ...referralFields() },
        locale: input.locale,
      }),
    onSuccess: (result) => {
      // A local development deployment answers with the account it signed in
      // instead of a code, so there is no second step to complete.
      if (result.user) {
        signedIn(result.user)
      }
    },
  })
}

/**
 * Caches a freshly signed-in account.
 *
 * Every previously fetched answer belonged to the anonymous visitor (a share
 * link page knows whether the viewer joined, for instance), so the cache is
 * invalidated instead of being patched key by key.
 */
function useSignedIn() {
  const queryClient = useQueryClient()
  const setUser = useSessionStore((state) => state.setUser)

  return (user: UserDto) => {
    setUser(user)
    queryClient.setQueryData(queryKeys.me, user)
    void queryClient.invalidateQueries()
  }
}

/**
 * Completes an email sign-in.
 *
 * The captured referral code rides along in the body: the cookie is issued by the
 * response, and attribution has to happen in the same request that may create
 * the account.
 */
export function useVerifyCode() {
  const signedIn = useSignedIn()

  return useMutation({
    mutationFn: (input: { email: string; code: string; locale: Locale }) =>
      apiRequest<SignInResponse>('/api/auth/email/verify', {
        method: 'POST',
        body: {
          email: input.email,
          code: input.code,
          locale: input.locale,
          ...referralFields(),
        },
        locale: input.locale,
      }),
    onSuccess: (result) => signedIn(result.user),
  })
}

/**
 * Completes a GitHub sign-in.
 *
 * Only the one-time ticket is exchanged here. The referral code never reaches
 * this request: the account is created during the provider callback, which can
 * only read the signed `state` that `/api/auth/github/start` embedded, so the
 * code travels on the start link instead.
 */
export function useExchangeGithubTicket() {
  const signedIn = useSignedIn()

  return useMutation({
    mutationFn: (ticket: string) =>
      apiRequest<SignInResponse>('/api/auth/github/exchange', {
        method: 'POST',
        body: { ticket },
        // A rejected ticket answers 401, which the API client would otherwise
        // read as an expired session: a signed-in visitor following a stale
        // sign-in link would be dropped locally while the server session is
        // still valid. The page renders the failure itself.
        tolerateUnauthorized: true,
      }),
    onSuccess: (result) => signedIn(result.user),
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  const clear = useSessionStore((state) => state.clear)

  return useMutation({
    mutationFn: () =>
      // Sign-out is already dropping every local trace, so a rejected call must
      // not trigger the global expiry redirect on top of it.
      apiRequest<void>('/api/auth/logout', { method: 'POST', tolerateUnauthorized: true }),
    onSettled: () => {
      clear()
      queryClient.clear()
      // The wizard drafts live in the browser rather than in the account, so signing
      // out has to drop them: otherwise the next account opens the wizard pre-filled
      // with the previous one's answers.
      clearAllDrafts()
    },
  })
}

/**
 * Saves the account's own fields.
 *
 * The answer is written to both places the account is read from: the store the
 * header renders from and the `me` query the boot probe owns. Writing only one
 * of them leaves the other describing the previous nickname until something
 * happens to refetch it.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const setUser = useSessionStore((state) => state.setUser)

  return useMutation({
    mutationFn: async (input: { nickname?: string | null; locale?: Locale }) => {
      const result = await apiRequest<CurrentUserResponse>('/api/auth/me', {
        method: 'PATCH',
        body: input,
      })
      setUser(result.user)
      queryClient.setQueryData(queryKeys.me, result.user)
      return result.user
    },
  })
}
